import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../../Contexts/AuthContext';
import CONFIG from '../../Services/CONFIG';
import { CALL_STATUS, SAMPLE_RATE } from './constants';
import { CallStatus, RealtimeMessage, TextOutputSource } from './types';
import { useParams } from 'react-router-dom';

/**
 * Convert Float32Array audio samples to base64-encoded PCM16 (little-endian)
 */
function float32ToPcm16Base64(float32Array: Float32Array): string {
  const pcm16 = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  const bytes = new Uint8Array(pcm16.buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert base64-encoded PCM16 (little-endian) to Float32Array
 */
function pcm16Base64ToFloat32(base64: string): Float32Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const pcm16 = new Int16Array(bytes.buffer);
  const float32 = new Float32Array(pcm16.length);
  for (let i = 0; i < pcm16.length; i++) {
    float32[i] = pcm16[i] / (pcm16[i] < 0 ? 0x8000 : 0x7fff);
  }
  return float32;
}

/**
 * Hook for realtime audio WebSocket communication with the agent
 */
export function useRealtimeAudioCall(
  agentId: string,
  options?: {
    onTranscriptMessage?: (text: string, source: TextOutputSource) => void;
    onSessionCreated?: (sessionKey: string) => void;
    voiceProfileId?: string;
  },
) {
  const { organizationId } = useParams();

  const onTranscriptMessageRef = useRef(options?.onTranscriptMessage);
  useEffect(() => {
    onTranscriptMessageRef.current = options?.onTranscriptMessage;
  }, [options?.onTranscriptMessage]);

  const onSessionCreatedRef = useRef(options?.onSessionCreated);
  useEffect(() => {
    onSessionCreatedRef.current = options?.onSessionCreated;
  }, [options?.onSessionCreated]);

  const { getAccessTokenSilently } = useAuth();

  const [isCallActive, setIsCallActive] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [callStatus, setCallStatus] = useState<CallStatus>(CALL_STATUS.idle);
  const [transcriptText, setTranscriptText] = useState<string>('');

  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);

  const audioQueueRef = useRef<Float32Array[]>([]);
  const isPlayingRef = useRef<boolean>(false);
  const nextPlayTimeRef = useRef<number>(0);

  // Build WebSocket URL from config
  const getWebSocketUrl = useCallback(() => {
    const baseUrl = CONFIG.SERVER_ASSISTANT;
    // Convert HTTP(S) to WS(S)
    const wsUrl = baseUrl.replace(/^http/, 'ws');
    return `${wsUrl}/v1/agents/realtime/ws`;
  }, []);

  // Play audio chunk
  const playAudioChunk = useCallback((float32Data: Float32Array) => {
    if (!audioContextRef.current) return;

    const ctx = audioContextRef.current;
    const buffer = ctx.createBuffer(1, float32Data.length, SAMPLE_RATE);
    buffer.getChannelData(0).set(float32Data);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);

    const now = ctx.currentTime;
    const startTime = Math.max(now, nextPlayTimeRef.current);
    source.start(startTime);
    nextPlayTimeRef.current = startTime + buffer.duration;
  }, []);

  // Process received WebSocket messages
  const handleWebSocketMessage = useCallback(
    (event: MessageEvent<string>) => {
      let data: unknown;

      try {
        data = JSON.parse(event.data);
      } catch (err) {
        console.error('Invalid JSON:', err);
        return;
      }

      const message = data as RealtimeMessage;

      switch (message.type) {
        case 'audio.output':
          if (message.audio) {
            const float32 = pcm16Base64ToFloat32(message.audio);
            playAudioChunk(float32);
          }
          break;

        case 'text.output':
          if (message.text) {
            setTranscriptText((prev) => prev + (prev ? '\n' : '') + message.text);
            onTranscriptMessageRef.current?.(message.text, message.source ?? 'synthesis');
          }
          break;

        case 'session.created':
          if (message.session_key) {
            onSessionCreatedRef.current?.(message.session_key);
          }
          break;

        case 'error':
          console.error('WebSocket error:', message.message);
          setCallStatus(CALL_STATUS.error);
          break;

        default:
          console.warn('Unknown message type:', message);
          break;
      }
    },
    [playAudioChunk],
  );

  // Setup microphone input and audio processing
  const setupAudioInput = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      mediaStreamRef.current = stream;

      // Create audio context for input processing
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: SAMPLE_RATE,
      });
      audioContextRef.current = ctx;

      const source = ctx.createMediaStreamSource(stream);
      sourceNodeRef.current = source;

      // Create ScriptProcessor for capturing audio (or AudioWorklet if available)
      const bufferSize = 4096;
      const processor = ctx.createScriptProcessor(bufferSize, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (e: AudioProcessingEvent) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

        const inputData = e.inputBuffer.getChannelData(0);
        const base64Audio = float32ToPcm16Base64(inputData);

        wsRef.current.send(
          JSON.stringify({
            type: 'audio.input',
            audio: base64Audio,
          }),
        );
      };

      source.connect(processor);
      processor.connect(ctx.destination);

      return true;
    } catch (err) {
      console.error('Error setting up audio input:', err);
      return false;
    }
  }, []);

  // Cleanup audio resources
  const cleanupAudio = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    audioQueueRef.current = [];
    isPlayingRef.current = false;
    nextPlayTimeRef.current = 0;
  }, []);

  // Start the call
  const startCall = useCallback(async () => {
    if (isCallActive || isConnecting) return;

    setIsConnecting(true);
    setCallStatus(CALL_STATUS.connecting);
    setTranscriptText('');

    try {
      // Get auth token
      const token = await getAccessTokenSilently();

      // Setup audio before connecting
      const audioReady = await setupAudioInput();
      if (!audioReady) {
        throw new Error('Failed to setup audio input');
      }

      // Create WebSocket connection
      const wsUrl = getWebSocketUrl();
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected, sending session.create');

        // Send authentication message
        ws.send(
          JSON.stringify({
            type: 'session.create',
            auth_token: token,
            assistant_key: agentId,
            voice_profile_key: options?.voiceProfileId ?? undefined,
            x_organization_key: organizationId,
          }),
        );

        setIsCallActive(true);
        setIsConnecting(false);
        setCallStatus(CALL_STATUS.active);
      };

      ws.onmessage = handleWebSocketMessage;

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setCallStatus(CALL_STATUS.error);
        setIsConnecting(false);
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsCallActive(false);
        setIsConnecting(false);
        setCallStatus(CALL_STATUS.idle);
        cleanupAudio();
      };
    } catch (err) {
      console.error('Error starting call:', err);
      setCallStatus(CALL_STATUS.error);
      setIsConnecting(false);
      cleanupAudio();
    }
  }, [
    agentId,
    isCallActive,
    isConnecting,
    getAccessTokenSilently,
    getWebSocketUrl,
    setupAudioInput,
    handleWebSocketMessage,
    cleanupAudio,
  ]);

  // End the call
  const endCall = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    cleanupAudio();
    setIsCallActive(false);
    setIsConnecting(false);
    setCallStatus(CALL_STATUS.idle);
  }, [cleanupAudio]);

  // Toggle call state
  const toggleCall = useCallback(() => {
    if (isCallActive) {
      endCall();
    } else {
      startCall();
    }
  }, [isCallActive, startCall, endCall]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      endCall();
    };
  }, [endCall]);

  return {
    isCallActive,
    isConnecting,
    callStatus,
    transcriptText,
    startCall,
    endCall,
    toggleCall,
  };
}

export default useRealtimeAudioCall;
